/**
 * Copyright (c) 2022, ResMed Inc. All rights reserved.
 */

import { React, useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Modal from '@material-ui/core/Modal';
import Typography from '@material-ui/core/Typography';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import ReactJson from 'react-json-view'
import FormGroup from '@material-ui/core/FormGroup';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import responseTemplate from '../../assets/templateResponse.json';
import scheduleTemplate from '../../assets/templateSchedule.json';
import {CloseButton, SaveButton} from "../common/CommonButtons";
import DynamoDBService from "../../js/service/DynamoDBService";
import {useAlert} from "react-alert";
import '../../css/views/CreateResponseJsonView.css'
import '../../css/views/Views.css'
import HttpService from "../../js/service/HttpService";

/**
 * Local Constants
 */

export default function ResponseJsonView(props) {

    const alert = useAlert();

    const httpService = new HttpService(props.oktaAuth);
    const dbService = new DynamoDBService(httpService);

    const [createdJson, setCreatedJson] = useState(responseTemplate);
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);
    const [jsonEditor, setJsonEditor] = useState(true);
    const [isValidJson, setIsValidJson] = useState(true);

    /**
     * Functions
     */
    function plainTextEditor(createdJson, setCreatedJson, setJsonString) {
        return (
            <TextareaAutosize
                rowsMin={4}
                style={{width: "70%"}}
                defaultValue={JSON.stringify(createdJson,null, 2)}
                spellCheck={"false"}
                onChange={(event) => {
                    setJsonString(event.target.value);
                    setCreatedJson(JSON.parse(event.target.value));
                }}
            />
        );
    }

    function jsonViewEditor(createdJson, setCreatedJson) {
        return (
            <ReactJson
                onEdit={(edit) => {
                    setCreatedJson(edit.updated_src);
                }}
                enableClipboard={false}
                src={createdJson}
                displayDataTypes={false}
                style={{fontSize: 17}}
            />
        );
    }

    const getTemplate = () => {
        return props.isResponse() ? responseTemplate : scheduleTemplate;
    }

    const [jsonString, setJsonString] = useState(JSON.stringify(getTemplate()));

    const validateJsonFormat = (responseString) => {
        try {
            JSON.parse(responseString);
        } catch (e) {
            setIsValidJson(false);
            return false;
        }
        setIsValidJson(true);
        return true;
    }

    /**
     * Handles click on publish button
     * Attempts to publish, alerts with response and refreshes the table if needed.
     * Closes after finishing.
     */
    const handlePublish = () => {
        dbService.put(createdJson, alert, props.updateTable);

        handleClose();
        handleDialogClose();
    };

    const handleClose = () => {
        setCreatedJson(getTemplate());
        props.setOpen(false);
    }

    const handleDialogOpen = () => {
        setWarningDialogOpen(true);
    }
    const handleDialogClose = () => {
        setWarningDialogOpen(false);
    }

    useEffect(() => {
        setCreatedJson(getTemplate());
    }, [props.open])

    /**
     * Html
     */
    return (
        <>
            <Modal
                aria-labelledby="transition-modal-title"
                className='views-common-modal'
                open={props.open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={props.open}>
                    <div className='views-common-paper'>
                        <div className='views-common-header'>
                            <>
                                <Typography variant="h5" style={{ margin: "10px" }} gutterBottom>
                                    Create a Response
                                </Typography>
                                <Typography variant="body2" style={{ margin: "0 0 20px 10px" }}>
                                    {jsonEditor ? "Ctrl + Click or use the icon to edit a value. " :
                                        "Edit the text area to change the survey response. "}
                                    Click <b>Publish</b> to add the object to the DB
                                </Typography>
                            </>
                            <FormGroup row>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={jsonEditor}
                                            onChange={() => {
                                                setJsonString(JSON.stringify(createdJson));
                                                setJsonEditor(!jsonEditor);
                                            }}
                                            name="jsonEditor"
                                            color="primary"
                                        />
                                    }
                                    label="JSON Editor"
                                />
                                <SaveButton
                                    clickHandler={() => {
                                        if (!jsonEditor) {
                                            validateJsonFormat(jsonString);
                                        }
                                        handleDialogOpen();
                                    }}
                                    saveType="Publish"
                                />
                                <CloseButton handleClose={handleClose} />
                            </FormGroup>
                        </div>
                        <div className='createJsonView-jsonEditor'>
                            {jsonEditor ? jsonViewEditor(createdJson, setCreatedJson) :
                                plainTextEditor(createdJson, setCreatedJson, setJsonString)}
                        </div>
                        <Dialog
                            open={warningDialogOpen}
                            onClose={handleDialogClose}
                        >
                            <DialogTitle >{"Put Response in DynamoDB?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Are you sure you want to add this survey response to the database?
                                </DialogContentText>
                                {isValidJson ? (
                                    <DialogContentText style={{ color: "#00DB3C" }}>
                                        ✓ JSON string was successfully validated
                                    </DialogContentText>) : (
                                    <DialogContentText style={{ color: "#EE0004"}}>
                                        Formatting validation failed. Please check your JSON string.
                                    </DialogContentText>)}
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleDialogClose}
                                    color="primary"
                                >
                                    Return
                                </Button>
                                <Button
                                    onClick={handlePublish}
                                    color="primary"
                                    autoFocus
                                    disabled={!jsonEditor && !isValidJson}
                                >
                                    Confirm
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </div>
                </Fade>
            </Modal>
        </>
    );
}